// Este código é opcional e é usado para registar um service worker.
// register() não é chamado por defeito.

// Isto permite que a aplicação carregue mais rapidamente em visitas subsequentes em produção e
// dá-lhe capacidades offline. No entanto, também significa que os programadores (e utilizadores)
// só verão as atualizações implementadas em visitas subsequentes a uma página, depois de todos os
// separadores existentes abertos na página terem sido fechados, uma vez que os recursos previamente
// em cache são atualizados em segundo plano.

import { buildFirebaseServiceWorkerUrl } from './config/firebaseEnvironment';
import { firebaseConfig, firebaseEnvironment, isE2EEnvironment } from './services/firebase';

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    // [::1] é o endereço IPv6 do localhost.
    window.location.hostname === '[::1]' ||
    // 127.0.0.0/8 são considerados localhost para IPv4.
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

const MIGRATION_KEY = 'sisfol_sw_migration_fcm_v2';
const ACTIVE_WORKER_NAME = '/firebase-messaging-sw.js';

const isExpectedFirebaseWorker = (scriptUrl, expectedWorkerUrl) => {
  if (!scriptUrl || !expectedWorkerUrl) return false;

  try {
    const currentUrl = new URL(scriptUrl, window.location.href);
    const expectedUrl = new URL(expectedWorkerUrl, window.location.href);

    return currentUrl.origin === expectedUrl.origin
      && currentUrl.pathname === ACTIVE_WORKER_NAME
      && currentUrl.pathname === expectedUrl.pathname
      && currentUrl.searchParams.get('sisdpEnvironment') === expectedUrl.searchParams.get('sisdpEnvironment')
      && currentUrl.searchParams.get('firebaseConfig') === expectedUrl.searchParams.get('firebaseConfig');
  } catch (_error) {
    return false;
  }
};

const migrateLegacyWorkersOnce = async (expectedWorkerUrl) => {
  if (localStorage.getItem(MIGRATION_KEY) === 'done') return;

  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(registrations.map((registration) => {
    const scriptUrls = [
      registration.active?.scriptURL,
      registration.waiting?.scriptURL,
      registration.installing?.scriptURL,
    ].filter(Boolean);

    const firebaseWorkerUrls = scriptUrls.filter((scriptUrl) => {
      try {
        return new URL(scriptUrl, window.location.href).pathname === ACTIVE_WORKER_NAME;
      } catch (_error) {
        return false;
      }
    });

    if (firebaseWorkerUrls.length === 0) return Promise.resolve(false);

    const hasUnexpectedFirebaseWorker = firebaseWorkerUrls.some(
      (scriptUrl) => !isExpectedFirebaseWorker(scriptUrl, expectedWorkerUrl)
    );

    return hasUnexpectedFirebaseWorker
      ? registration.unregister()
      : Promise.resolve(false);
  }));

  localStorage.setItem(MIGRATION_KEY, 'done');
};

export function register(config) {
  if (isE2EEnvironment) return;
  // --- CORREÇÃO APLICADA ---
  // Alterado para registar o Service Worker em desenvolvimento (localhost) também,
  // o que é essencial para testar as notificações push.
  if ('serviceWorker' in navigator) {
    // O construtor de URL está disponível em todos os navegadores que suportam SW.
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      // O nosso service worker não funcionará se PUBLIC_URL estiver num domínio diferente
      // daquele em que a nossa página é servida.
      return;
    }

    window.addEventListener('load', () => {
      // --- CORREÇÃO APLICADA ---
      // Aponta diretamente para o service worker do Firebase Messaging.
      const swUrl = buildFirebaseServiceWorkerUrl(
        `${process.env.PUBLIC_URL}/firebase-messaging-sw.js`,
        { environment: firebaseEnvironment, firebaseConfig }
      );

      migrateLegacyWorkersOnce(swUrl).catch((error) => {
        console.warn('Não foi possível concluir a migração do Service Worker antigo:', error);
      }).finally(() => {
      if (isLocalhost) {
        // Em localhost, regista diretamente o nosso service worker.
        registerValidSW(swUrl, config);

        navigator.serviceWorker.ready.then(() => {
          // A linha abaixo foi comentada para limpar o console de desenvolvimento.
          // console.log('Firebase Messaging service worker registered successfully on localhost.');
        });
      } else {
        // Em produção, regista o service worker.
        registerValidSW(swUrl, config);
      }
      });
    });
  }
}

function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              console.log(
                'New content is available and will be used when all ' +
                  'tabs for this page are closed. See https://cra.link/PWA.'
              );

              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }

              // Este worker é dedicado ao Firebase Messaging e não mantém
              // precache da aplicação. Ativá-lo imediatamente evita que uma
              // sessão longa continue presa a uma versão antiga do handler FCM.
              registration.waiting?.postMessage({type: 'SISFOL_ACTIVATE_SERVICE_WORKER'});
            } else {
              console.log('Content is cached for offline use.');

              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };
    })
    .catch((error) => {
      console.error('Error during service worker registration:', error);
    });
}

// A função checkValidServiceWorker foi removida por ser desnecessária para este caso de uso específico.

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}
